/**
 * ToggleModule
 *
 * Finds elements with a `.readmore` class and toggles the visibility of the
 * next sibling element (or next element in the same container) with a
 * `.toggled-module` class. No dependency on Divi module markup.
 *
 * Optional: Provide custom trigger text via data attributes on the trigger:
 * - data-collapsed-text: text when content is collapsed (default state)
 * - data-expanded-text: text when content is expanded
 */
const ToggleModule = {
	init() {
		const triggers = document.querySelectorAll(".readmore");
		if (!triggers.length) {
			return;
		}

		// Bind the context of `this` for the event handler.
		this.handleToggle = this.handleToggle.bind(this);
		this.handleKeydown = this.handleKeydown.bind(this);

		

		triggers.forEach((trigger) => {
			// Make non-button triggers accessible
			if (!trigger.hasAttribute("role")) trigger.setAttribute("role", "button");
			if (!trigger.hasAttribute("tabindex")) trigger.setAttribute("tabindex", "0");

			// Initialize ARIA state based on target
			const target = this.findTarget(trigger);
			if (target) {
				const isOpen = target.classList.contains("is-open");
				trigger.setAttribute("aria-expanded", isOpen ? "true" : "false");
				target.setAttribute("aria-hidden", isOpen ? "false" : "true");
				// Initialize text to match current state
				this.updateTriggerText(trigger, isOpen);
			} else {
				try {
					console.debug("[ToggleModule] init: no target found for trigger", trigger);
				} catch (e) { /* noop */ }
			}

			trigger.addEventListener("click", this.handleToggle);
			trigger.addEventListener("keydown", this.handleKeydown);
		});
	},

	findTarget(trigger) {
		// 0) explicit selector via data-target
		const explicit = trigger.getAttribute("data-target");
		if (explicit) {
			try {
				const t = document.querySelector(explicit);
				if (t) return t;
			} catch (e) {
				// invalid selector; ignore
			}
		}

		// 1) scan forward siblings of the trigger
		let el = trigger.nextElementSibling;
		while (el) {
			if (el.classList && el.classList.contains("toggled-module")) return el;
			el = el.nextElementSibling;
		}

		// 2) if not found, climb ancestors; for each ancestor, scan its next siblings
		let ancestor = trigger.parentElement;
		while (ancestor && ancestor !== document.body) {
			let sib = ancestor.nextElementSibling;
			while (sib) {
				if (sib.classList && sib.classList.contains("toggled-module")) return sib;
				sib = sib.nextElementSibling;
			}
			ancestor = ancestor.parentElement;
		}

		return null;
	},

	handleToggle(event) {
		const trigger = event.currentTarget;
		const targetModule = this.findTarget(trigger);

		// Debug: clicked trigger and whether a target was found
		try {
			//console.debug("[ToggleModule] click: trigger", trigger);
			if (!targetModule) console.debug("[ToggleModule] click: no .toggled-module found after trigger");
		} catch (e) { /* noop */ }

		if (!targetModule) return;

		// Toggle a class on the target to show/hide it with CSS.
		const isOpen = targetModule.classList.toggle("is-open");

		// Update ARIA state
		trigger.setAttribute("aria-expanded", isOpen ? "true" : "false");
		targetModule.setAttribute("aria-hidden", isOpen ? "false" : "true");

		

		// Update the trigger text
		this.updateTriggerText(trigger, isOpen);
	},

	updateTriggerText(trigger, isOpen) {
		const expandedText = trigger.getAttribute("data-expanded-text");
		const collapsedText = trigger.getAttribute("data-collapsed-text");
		if (expandedText && collapsedText) {
			trigger.textContent = isOpen ? expandedText : collapsedText;
			return;
		}
		// Default behavior for .readmore components
		trigger.textContent = isOpen ? "READ LESS >" : "READ MORE >";
	},

	handleKeydown(event) {
		const { key } = event;
		if (key === "Enter" || key === " ") {
			event.preventDefault();
			this.handleToggle(event);
		}
	},
};

export default ToggleModule;